/**
 * Shared reachable-partners UI: inline preview row + popover modal +
 * shared fetch hook. Used by both AccountsTable and ProgramCards.
 *
 * Why a portal for the modal: when a fixed-position modal is rendered
 * inside a card whose ancestors apply `transform` / `filter` / `will-change`,
 * those properties become the containing block for `position: fixed` —
 * the modal gets clipped/positioned to that card instead of the viewport.
 * Rendering into document.body via createPortal sidesteps that entirely.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

// How many partner names show inline before the "+N more →" link.
export const PARTNERS_INLINE_LIMIT = 5;

/**
 * Fetches /api/settings + /api/programs/reachable once on mount.
 * Returns { showReachable, reachable }.
 */
export function useReachable() {
  const [showReachable, setShowReachable] = useState(false);
  const [reachable, setReachable] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: s }, { data: r }] = await Promise.all([
          api.get("/settings"),
          api.get("/programs/reachable"),
        ]);
        if (cancelled) return;
        setShowReachable(!!s?.show_reachable_partners);
        setReachable(r?.programs || {});
      } catch {
        // silent: the row simply doesn't render
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { showReachable, reachable };
}

/**
 * Inline preview row. Returns null when there's nothing to show.
 *
 * Props:
 *   program    — the user's program object (must have id, code, name)
 *   reach      — entry from useReachable().reachable, e.g.
 *                { alliance, alliance_partners: [...], transfer_partners: [...] }
 *   onOpenMore — called with { kind:"alliance"|"transfer", program, alliance, partners }
 *                when the user clicks "+N more →"
 *   variant    — "card" (multi-line, more whitespace) or "row" (one-liner, compact)
 */
export function ReachableRow({ program, reach, onOpenMore, variant = "card" }) {
  if (!reach) return null;
  const alliance = reach.alliance;
  const ally     = reach.alliance_partners || [];
  const xfer     = reach.transfer_partners || [];
  if (ally.length === 0 && xfer.length === 0) return null;

  const block = (kind, partners, label) => {
    if (partners.length === 0) return null;
    const head = partners.slice(0, PARTNERS_INLINE_LIMIT);
    const more = partners.length - head.length;
    return (
      <div className={variant === "row"
        ? "text-[11.5px] text-neutral-600 leading-snug truncate"
        : "mt-3 text-[12px] text-neutral-600 leading-snug"}>
        <span className={variant === "row"
          ? "font-semibold text-neutral-700"
          : "block font-semibold text-neutral-700"}>
          {label}
        </span>
        <span className={variant === "row" ? " " : "block mt-0.5"}>
          {variant === "row" && " "}
          {head.map((p, i) => (
            <span key={p.slug}>
              {i > 0 && <span className="text-neutral-300"> · </span>}
              <span>{p.name}</span>
            </span>
          ))}
          {more > 0 && (
            <>
              {" "}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMore({ kind, program, alliance, partners });
                }}
                className="text-[11.5px] font-semibold text-neutral-500 hover:text-black"
                data-testid={`reach-more-${kind}-${program.code}`}
              >
                +{more} more →
              </button>
            </>
          )}
        </span>
      </div>
    );
  };

  return (
    <div data-testid={`reach-row-${program.code}`}>
      {block("alliance", ally, alliance ? `Also redeem on ${alliance} partners` : "Alliance partners")}
      {block("transfer", xfer, "Transfer points to")}
    </div>
  );
}

/**
 * Full-screen modal listing one or more partner sections for a program.
 * Renders via a portal into document.body so it isn't trapped inside
 * any ancestor's stacking/containing context.
 *
 * Two payload shapes (both supported for back-compat with "+N more" links):
 *
 *   { program, sections: [{ title, subtitle, partners }] }      ← preferred
 *   { kind, program, alliance, partners }                       ← legacy, one section
 *
 * The single-section legacy shape is auto-promoted to a sections array.
 */
export function PartnersModal({ payload, onClose }) {
  if (!payload) return null;
  const program = payload.program;
  const sections = payload.sections || [
    {
      title:
        payload.kind === "alliance"
          ? (payload.alliance ? `${payload.alliance} partners` : "Alliance partners")
          : "Transfer partners",
      subtitle:
        payload.kind === "alliance"
          ? `Your ${program.name} miles also book seats on these airlines.`
          : `Your ${program.name} points transfer to these programs.`,
      partners: payload.partners || [],
    },
  ];

  const node = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 fade-in"
      style={{ background: "rgba(10,10,10,0.55)" }}
      onClick={onClose}
      data-testid="partners-modal"
    >
      <div
        className="card w-full max-w-md p-6 relative max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-400 hover:text-black"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="font-display font-bold text-[16px] tracking-tight pr-6">{program.name}</div>

        {sections.filter((s) => (s.partners || []).length > 0).map((s, idx) => (
          <div key={idx} className="mt-5 first:mt-3">
            <div className="font-semibold text-[13.5px] tracking-tight">{s.title}</div>
            {s.subtitle && <div className="text-[12px] text-neutral-600 mt-0.5">{s.subtitle}</div>}
            <ul className="mt-2 space-y-1">
              {s.partners.map((p) => (
                <li
                  key={p.slug}
                  className="text-[13px] py-1 border-b border-black/[0.04] last:border-0"
                >
                  {p.name}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(node, document.body)
    : node;
}

/**
 * Small info icon button rendered next to a program name. Opens the
 * full PartnersModal with both alliance + transfer sections (whichever
 * are non-empty). Returns null if the program has no reachable info.
 *
 * Props:
 *   program    — the user's program object
 *   reach      — entry from useReachable().reachable[program.id]
 *   onOpenMore — same signature as ReachableRow's onOpenMore; receives the
 *                full multi-section payload to surface in the modal
 */
export function ReachableInfoButton({ program, reach, onOpenMore }) {
  if (!reach) return null;
  const ally = reach.alliance_partners || [];
  const xfer = reach.transfer_partners || [];
  if (ally.length === 0 && xfer.length === 0) return null;

  const sections = [];
  if (ally.length > 0) {
    sections.push({
      title: reach.alliance ? `${reach.alliance} partners` : "Alliance partners",
      subtitle: `Your ${program.name} miles also book seats on these airlines.`,
      partners: ally,
    });
  }
  if (xfer.length > 0) {
    sections.push({
      title: "Transfer points to",
      subtitle: `Your ${program.name} points transfer to these programs.`,
      partners: xfer,
    });
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenMore({ program, sections });
            }}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-neutral-500 hover:text-black hover:bg-black/[0.06] shrink-0 transition-colors"
            aria-label="Where you can use these points"
            data-testid={`reach-info-${program.code}`}
          >
            <Info size={16} strokeWidth={2.25} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-neutral-900 text-white border-none shadow-lg">
          Where you can use these points
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
